import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import Toast from '../../components/Toast';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [tools, setTools] = useState([]);
  const [toast, setToast] = useState(null);
  const [permModal, setPermModal] = useState(null);
  const [rolePerms, setRolePerms] = useState([]);
  const [modal, setModal] = useState(null); // null | 'new' | roleId
  const [form, setForm] = useState({ name: '', description: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [r, t] = await Promise.all([api.get('/roles'), api.get('/tools/all')]);
    setRoles(r);
    setTools(t);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modal === 'new') {
        await api.post('/roles', form);
        setToast({ message: 'Rol creado', type: 'success' });
      } else {
        await api.put(`/roles/${modal}`, form);
        setToast({ message: 'Rol actualizado', type: 'success' });
      }
      setModal(null);
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const toggle2FA = async (roleId, current) => {
    try {
      await api.put(`/roles/${roleId}/2fa`, { required: !current });
      setToast({ message: '2FA de rol actualizado', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const openPerms = async (roleId) => {
    const perms = await api.get(`/permissions/role/${roleId}`);
    setRolePerms(perms);
    setPermModal(roleId);
  };

  const savePerms = async () => {
    try {
      await api.put(`/permissions/role/${permModal}`, { toolIds: rolePerms });
      setToast({ message: 'Permisos de rol guardados', type: 'success' });
      setPermModal(null);
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const openEdit = (role) => {
    setForm({ name: role.name, description: role.description || '' });
    setModal(role.id);
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-gray-500">{roles.length} roles</span>
        <button onClick={() => { setForm({ name: '', description: '' }); setModal('new'); }}
          className="btn-accent text-sm px-4 py-2 rounded-lg">
          Nuevo rol
        </button>
      </div>

      <div className="space-y-3">
        {roles.map(r => (
          <div key={r.id} className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium capitalize">{r.name}</p>
                <p className="text-xs text-gray-400">{r.description || 'Sin descripción'}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => toggle2FA(r.id, r.require_2fa)}
                  className={`text-xs px-2 py-1 rounded-full ${r.require_2fa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  2FA: {r.require_2fa ? 'Obligatorio' : 'Opcional'}
                </button>
                <button onClick={() => openPerms(r.id)} className="text-xs font-medium hover:underline" style={{ color: '#3b82f6' }}>Permisos</button>
                <button onClick={() => openEdit(r)} className="text-xs font-medium hover:underline" style={{ color: '#FFB800' }}>Editar</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Role form modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{modal === 'new' ? 'Nuevo rol' : 'Editar rol'}</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input type="text" placeholder="ej: comercial" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                  required />
                <p className="text-xs text-gray-400 mt-1">Sin espacios ni mayúsculas. Ej: comercial, produccion, diseno</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input type="text" placeholder="Equipo comercial" value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Guardar</button>
                <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions modal */}
      {permModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPermModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">Herramientas asignadas al rol</h3>
            <div className="space-y-2 mb-4">
              {tools.map(t => (
                <label key={t.id} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={rolePerms.includes(t.id)}
                    onChange={e => {
                      if (e.target.checked) setRolePerms([...rolePerms, t.id]);
                      else setRolePerms(rolePerms.filter(id => id !== t.id));
                    }} className="rounded border-gray-300" />
                  {t.name}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={savePerms} className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Guardar</button>
              <button onClick={() => setPermModal(null)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
